import { createAsyncThunk } from '@reduxjs/toolkit'
import { Dispatch, AnyAction } from 'redux'

import { setTimesOptions, setDatesOptions } from '@widgets/Restaurant/Booking/model/slice'

import { API } from '@shared/api'
import { generateDatesOptions } from '@shared/lib/helpers/schedule/schedule'
import { TypeExtendedScheduleDay } from '@shared/types/schedule/types'
import { TypeMappingScheduleWeek } from '@shared/api/middleware/mappingAPI/restaurant/type'

import {
  TypeFetchRemarkedSlots,
  TypeInitFetchRemarkedSlotsNearTwoDays,
  TypeRestoplaceSlot,
  TypeRestoplaceSlotsResult,
  TypeInitFetchRestoplaceSlotsParams,
} from './types'

const isDevParam = typeof window === 'object' && window.location.search.indexOf('is_dev=true') > 0

/**
 * @function fetchRemarkedSlots - запрос слотов для ремаркеда
 */
export const fetchRemarkedSlots = createAsyncThunk(
  'booking/fetchRemarkedSlots',
  async (action: TypeFetchRemarkedSlots, { dispatch, getState }) => {
    const { guests, date, restSlug } = action
    isDevParam && console.log('!!! fetchRemarkedSlots: action', { guests, date, restSlug })

    return new Promise((resolve, reject) => {
      API.booking_get_slots({ numOfPlaces: guests, date: date.value, rest: restSlug })
        .then((res) => {
          isDevParam && console.log('!!! fetchRemarkedSlots: res', res)

          if (res.slots?.length) {
            const formattedDate = {
              itemText: new Date(date.value).toLocaleDateString(),
              itemTextShort: new Date(date.value).toLocaleDateString(),
              schedule: null,
              openRestDate: date.value.split('T')[0],
              value: date.value.split('T')[0],
              thisDate: date.value.split('T')[0],
              isRestOpened: true,
            }
            console.log('!!! fetchRemarkedSlots: formattedDate', formattedDate)
            resolve({ date: formattedDate, slots: res.slots })
          } else {
            reject({ date: date, slots: null })
          }
        })
        .catch((er) => reject({ date: date, slots: null }))
    })
  },
)

/**
 * @function initFetchRemarkedSlotsNearTwoDays
 * @description - запрос слотов для ремаркеда на ближайшие два дня
 */
export const initFetchRemarkedSlotsNearTwoDays = createAsyncThunk(
  'booking/initFetchRemarkedSlotsNearTwoDays',
  async (action: TypeInitFetchRemarkedSlotsNearTwoDays, { dispatch, getState }) => {
    const { guests, restSlug, schedule } = action
    const addZero = (number: number): string => `${`00${number}`.slice(-2)}`

    // генерируем даты для запроса
    const fetchArr = Array.from(Array(2).keys()).map((el, index) => {
      try {
        const fetchDate = new Date(Date.now())
        fetchDate.setDate(fetchDate.getDate() + index)

        const currentYear = fetchDate.getFullYear()
        const currentMonth = addZero(fetchDate.getMonth() + 1)
        const currentDay = addZero(fetchDate.getDate())
        const currentDateFormatted = `${currentYear}-${currentMonth}-${currentDay}`

        isDevParam && console.log('!!! initFetchRemarkedSlotsNearTwoDays: guests', guests)
        isDevParam && console.log('!!! initFetchRemarkedSlotsNearTwoDays: currentDateFormatted', currentDateFormatted)
        isDevParam && console.log('!!! initFetchRemarkedSlotsNearTwoDays: restSlug', restSlug)

        return API.booking_get_slots({ numOfPlaces: guests, date: currentDateFormatted, rest: restSlug })
      } catch (error) {
        console.error('Ошибка при получении слотов remarked:', error)
        return null
      }
    })

    // запрос слотов для ремаркеда на ближайшие два дня
    const response = await Promise.all(fetchArr)
    ;(await isDevParam) && console.log('!!! initFetchRemarkedSlotsNearTwoDays: response', response)

    // форматируем ответ
    const result = []
    if (response?.length) {
      response.forEach((daySlots) => {
        if (!daySlots.slots?.length) return
        const firstSlot = daySlots.slots[0]

        const dateOption = {
          itemText: new Date(firstSlot.date).toLocaleDateString(),
          itemTextShort: new Date(firstSlot.date).toLocaleDateString(),
          schedule: null,
          openRestDate: firstSlot.date,
          value: firstSlot.date,
          thisDate: firstSlot.date,
          isRestOpened: true,
        }
        result.push({ dateOption: dateOption, timeOptions: daySlots.slots })
      })
    }

    if (result.length) return result
    else throw Error()
  },
)

/**
 * @function fetchRestoplaceSlots - запрос слотов для рестоплейса
 */
export const fetchRestoplaceSlots = createAsyncThunk(
  'booking/fetchRestoplaceSlots',
  async (params: { guests: number; restSlug: string; date: TypeExtendedScheduleDay }, { dispatch }) => {
    const { guests, restSlug, date } = params

    try {
      console.log('fetchRestoplaceSlots params:', { guests, restSlug, date })

      // запрос слотов для рестоплейса
      const response = await API.booking_get_slots({
        numOfPlaces: guests,
        date: date.value,
        rest: restSlug,
      })

      console.log('fetchRestoplaceSlots response:', response)

      if (!response?.slots?.length) {
        console.error('Ошибка при получении слотов restoplace: нет данных о слотах')
        dispatch(setTimesOptions([]))
        return { date, slots: [] }
      }

      const slots = response.slots
        .filter((slot) => {
          // фильтруем слоты
          const isValid = slot?.value && slot?.free && slot?.counts?.[guests] === 'free'
          if (!isValid) {
            console.log('Слот отфильтрован:', slot)
          }
          return isValid
        })
        .map((slot) => {
          // форматируем ответ
          const result = {
            value: slot.value,
            label: slot.value,
            free: slot.free,
            counts: slot.counts,
            free_tables: slot.free_tables,
          }

          if (restSlug.indexOf('1600201') > -1) {
            isDevParam && console.log('!!! если секрет:', result)
          }

          return result
        })

      console.log('Итоговые слоты:', slots)

      if (slots.length === 0) {
        console.error('Ошибка при получении слотов restoplace: нет валидных слотов')
        dispatch(setTimesOptions([]))
        return { date, slots: [] }
      }

      // устанавливаем слотов для выбранного дня
      dispatch(setTimesOptions(slots))
      return { date, slots }
    } catch (error) {
      console.error('Ошибка при получении слотов restoplace:', error)
      dispatch(setTimesOptions([]))
      return { date, slots: [] }
    }
  },
)

/**
 * @function initFetchRestoplaceSlotsNearTwoDays
 * @description - запрос слотов для рестоплейса на ближайшие два дня
 */
export const initFetchRestoplaceSlotsNearTwoDays = createAsyncThunk<
  TypeRestoplaceSlotsResult[],
  TypeInitFetchRestoplaceSlotsParams,
  { dispatch: Dispatch<AnyAction> }
>(
  'booking/initFetchRestoplaceSlotsNearTwoDays',
  async (params: { guests: number; restSlug: string; schedule: any }, { dispatch }) => {
    const { guests, restSlug, schedule } = params

    try {
      console.log('initFetchRestoplaceSlotsNearTwoDays params:', { guests, restSlug, schedule })

      //  генерируем даты
      const dates = generateDatesOptions(schedule, { countDays: 2 })

      //  проверяем
      if (!dates || dates.length === 0) {
        console.error('Ошибка при инициализации слотов restoplace: нет доступных дат')
        dispatch(setDatesOptions([]))
        dispatch(setTimesOptions([]))
        return []
      }

      console.log('Сгенерированные даты:', dates)

      const slotsPromises = dates.map(async (date) => {
        console.log('Запрашиваем слоты для даты:', date)

        //  запрос слотов для рестоплейса
        const response = await API.booking_get_slots({
          numOfPlaces: guests,
          date: date.value,
          rest: restSlug,
        })

        isDevParam && console.log('!!! initFetchRestoplaceSlotsNearTwoDays: Получены слоты для даты:', date, response)

        //  есть ли слоты
        if (!response?.slots?.length) {
          return { dateOption: date, timeOptions: [] }
        }

        //  фильтруем слоты
        const slots = response.slots
          .filter((slot) => {
            // фильтруем слоты
            const isValid = slot?.value && slot?.free && slot?.counts?.[guests] === 'free'
            if (!isValid) {
              console.log('Слот отфильтрован:', slot)
            }
            return isValid
          })

          // форматируем
          .map((slot) => {
            const result = {
              value: slot.value,
              label: slot.value,
              free: slot.free,
              counts: slot.counts,
              free_tables: slot.free_tables,
            }

            if (restSlug.indexOf('1600201') > -1) {
              isDevParam && console.log('!!! если секрет:', result)
            }

            return result
          })

        console.log('Итоговые слоты для даты:', date, slots)
        return { dateOption: date, timeOptions: slots }
      })

      //  результат
      const results = await Promise.all(slotsPromises)
      console.log('Итоговые результаты:', results)
      return results
    } catch (error) {
      console.error('Ошибка при инициализации слотов restoplace:', error)
      dispatch(setDatesOptions([]))
      dispatch(setTimesOptions([]))
      return []
    }
  },
)
